import { useMemo, type CSSProperties } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  closeMeetingManagementTasks,
  getMeetingGroupOptions,
  getMeetingManagementDisplaySettings,
  getMeetingManagementTasks,
  getResponsibleOptions,
  getTaskAttachments,
  getTaskCreators,
  reopenMeetingManagementTask,
} from '../api/meetingManagementApi'
import type {
  CloseItem,
  MeetingManagementQuery,
  ReopenRequest,
  ResponsibleRole,
} from '../types'

/** Sorgu anahtarlarının kökü — mutasyonlar bunu tazeler. */
const ROOT = 'meeting-management'

/**
 * Görev listesi sunucu-durumu (105).
 *
 * <p>Aktif dil anahtara girer: durum **adı** sunucuda çözülür, dolayısıyla dil değişince tablo
 * yeniden çekilmelidir (mevcut görev listelerindeki desenin aynısı). `keepPreviousData` sayfa/süzgeç
 * değişiminde titremeyi önler.
 */
export function useMeetingManagementTasks(
  query: MeetingManagementQuery,
  /**
   * `enabled: false` → istek hiç gönderilmez. Onaylarım (131) görev sekmesini göremeyen
   * kullanıcıda sayı sorgusunu bununla kapatır: uç yetkisiz kullanıcıya 403 döner ve merkezî hata
   * bildirimi, kullanıcının hiç istemediği bir hatayı gösterirdi.
   */
  options?: { enabled?: boolean },
) {
  const { i18n } = useTranslation()
  return useQuery({
    enabled: options?.enabled ?? true,
    queryKey: [
      ROOT,
      'tasks',
      i18n.language,
      (query.statusIds ?? []).join(','),
      (query.delayedStatusIds ?? []).join(','),
      (query.onTimeStatusIds ?? []).join(','),
      query.creatorUserId ?? null,
      query.mainResponsibleEmployeeId ?? '',
      query.supportManagerEmployeeId ?? '',
      (query.meetingGroupIds ?? []).join(','),
      query.includeDirectTasks ?? true,
      query.includeSubTasks ?? false,
      query.page ?? 0,
      query.size ?? 20,
      query.sort ?? '',
    ],
    queryFn: () => getMeetingManagementTasks(query),
    placeholderData: keepPreviousData,
  })
}

/**
 * "Görevi Oluşturan" süzgecinin seçenekleri.
 *
 * <p>Anahtarda **ekranın süzgeçleri YOKTUR** ve bu bilinçlidir: seçenekler kullanıcı filtre
 * değiştirdikçe yeniden çekilmez ve kaybolmaz (FR-011). Aynı ilke kişi süzgeçleri için de geçerlidir.
 */
export function useTaskCreators() {
  return useQuery({ queryKey: [ROOT, 'creators'], queryFn: getTaskCreators })
}

/**
 * Tablonun yazı boyu ayarları. Anahtar {@link ROOT}'un dışında: kapat/yeniden aç sonrası tazelenmez,
 * değer nadiren değiştiği için ekranda kaldıkça yeniden çekilmez.
 */
export function useMeetingManagementDisplaySettings() {
  return useQuery({
    queryKey: ['meeting-management-display-settings'],
    queryFn: getMeetingManagementDisplaySettings,
    staleTime: Infinity,
  })
}

/**
 * Tablonun yazı boyu ayarlarını CSS değişkenlerine çevirir; ayar gelmezse CSS varsayılanı (12px) kalır.
 * Toplantı Yönetimi ile Onaylarım'ın Görev Kapatma sekmesi (131) aynı tabloyu çizer.
 *
 * <p>Alanlar TEK TEK denetlenir: THY ön ve arka ucu ayrı dağıtır; yeni ön uç `nameFontSize`
 * göndermeyen eski arka uçla buluşursa değişken `undefinedpx` olurdu — tanımlı ama geçersiz bir
 * değişken `var()` yedeğini de devre dışı bırakır ve başlık üst öğenin boyuna kayar.
 */
export function useMeetingManagementFontVars(): CSSProperties | undefined {
  const displaySettings = useMeetingManagementDisplaySettings()
  return useMemo(() => {
    const s = displaySettings.data
    if (!s) return undefined
    const sizes: [string, number | undefined][] = [
      ['--mm-note-font-size', s.completionNoteFontSize],
      ['--mm-desc-font-size', s.descriptionFontSize],
      ['--mm-name-font-size', s.nameFontSize],
    ]
    return Object.fromEntries(
      sizes.filter(([, px]) => typeof px === 'number').map(([name, px]) => [name, `${px}px`]),
    ) as CSSProperties
  }, [displaySettings.data])
}

/** "Toplantı Grubu" süzgecinin seçenekleri — anahtarda ekranın süzgeçleri yoktur (FR-011). */
export function useMeetingGroupOptions() {
  return useQuery({ queryKey: [ROOT, 'meeting-groups'], queryFn: getMeetingGroupOptions })
}

/**
 * Bir görevin dosya ekleri — ataç pop-up'ı açılınca çekilir.
 *
 * `taskId` null iken sorgu **hiç koşmaz** (`enabled`): pop-up kapalıyken istek atmak, tablodaki her
 * satır için boşuna bir çağrı demek olurdu. Anahtara görev kimliği girer, böylece iki farklı görevin
 * listesi birbirine karışmaz.
 */
export function useTaskAttachments(taskId: number | null) {
  return useQuery({
    queryKey: [ROOT, 'attachments', taskId],
    queryFn: () => getTaskAttachments(taskId as number),
    enabled: taskId != null,
  })
}

/** Kişi süzgeçlerinin seçenekleri — **rol başına ayrı** anahtar; iki liste birbirine karışmaz. */
export function useResponsibleOptions(role: ResponsibleRole) {
  return useQuery({
    queryKey: [ROOT, 'responsibles', role],
    queryFn: () => getResponsibleOptions(role),
  })
}

/**
 * Seçili görevleri toplu kapat. Başarıda liste **ve** oluşturan seçenekleri tazelenir: kapatılan
 * görevler varsayılan süzgeçte listeden düşer, o görevleri oluşturmuş bir kişinin başka görevi yoksa
 * süzgeç seçeneklerinden de düşer.
 */
export function useCloseTasks() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (items: CloseItem[]) => closeMeetingManagementTasks(items),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [ROOT] })
      // Görev listeleri ve panolar da aynı kayıtları gösteriyor.
      qc.invalidateQueries({ queryKey: ['actions'] })
    },
  })
}

/**
 * Bir görevi yeniden aç. **Görev başına tek istektir**: çoklu seçimde kuyruk pop-up'ta yürür ve her
 * görev kendi tarihini/notunu taşır.
 */
export function useReopenTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: ReopenRequest }) =>
      reopenMeetingManagementTask(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [ROOT] })
      qc.invalidateQueries({ queryKey: ['actions'] })
    },
  })
}
